import { useEffect, useState, useMemo } from "react";
import api, { formatIDR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { CurrencyCircleDollar } from "@phosphor-icons/react";

const monthLabel = (mk) => {
  const [y, m] = mk.split("-");
  const names = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
  return `${names[parseInt(m) - 1]} ${y}`;
};

const lastMonths = (n) => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const y = d.getFullYear(), m = d.getMonth() + 1;
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
};

export default function Commission() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/commission?month=${month}`).then((r) => setData(r.data)); }, [month]);

  const total = useMemo(() => {
    if (!data) return { commission: 0, sale: 0, purchase: 0 };
    return data.data.reduce((a, r) => ({
      commission: a.commission + r.commission,
      sale: a.sale + r.count_sale,
      purchase: a.purchase + r.count_purchase,
    }), { commission: 0, sale: 0, purchase: 0 });
  }, [data]);

  const months = lastMonths(12);

  return (
    <div>
      <PageHeader title="Komisi Makelar" subtitle="Rekap komisi makelar per bulan dari transaksi penjualan & pembelian" />
      <div className="p-4 md:p-6 lg:p-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-gray-200 -m-px mb-6">
          <div className="border border-gray-200 p-6">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-2">Bulan</div>
            <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="month-picker" className="w-full font-mono-num border border-gray-300 rounded-none p-2 text-sm">
              {months.map((m) => (<option key={m} value={m}>{monthLabel(m)}</option>))}
            </select>
          </div>
          <div className="border border-gray-200 p-6">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-2">Transaksi Bermakelar</div>
            <div className="font-mono-num text-2xl font-semibold">{total.sale + total.purchase}</div>
            <div className="text-[11px] text-gray-500 mt-1 font-mono-num">Jual {total.sale} · Beli {total.purchase}</div>
          </div>
          <div className="border border-gray-200 p-6 bg-orange-50">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-2">Total Komisi Bulan Ini</div>
            <div className="font-mono-num text-2xl font-bold text-[#F97316]" data-testid="total-commission">{formatIDR(total.commission)}</div>
          </div>
        </div>

        <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                <th className="p-4">Makelar</th>
                <th className="p-4 text-right">Jual</th>
                <th className="p-4 text-right">Beli</th>
                <th className="p-4 text-right">Omset Jual</th>
                <th className="p-4 text-right">Nilai Beli</th>
                <th className="p-4 text-right">Total Komisi</th>
              </tr>
            </thead>
            <tbody>
              {(data?.data || []).map((row) => (
                <tr key={row.makelar_name} className="border-b border-gray-100" data-testid={`commission-row-${row.makelar_name}`}>
                  <td className="p-4 font-semibold">
                    <div className="flex items-center gap-2">
                      <CurrencyCircleDollar size={16} weight="regular" className="text-gray-500" />
                      {row.makelar_name}
                    </div>
                  </td>
                  <td className="p-4 text-right font-mono-num">{row.count_sale}</td>
                  <td className="p-4 text-right font-mono-num">{row.count_purchase}</td>
                  <td className="p-4 text-right font-mono-num text-xs text-gray-600">{formatIDR(row.revenue_sale)}</td>
                  <td className="p-4 text-right font-mono-num text-xs text-gray-600">{formatIDR(row.revenue_purchase)}</td>
                  <td className="p-4 text-right font-mono-num font-bold text-[#F97316]">{formatIDR(row.commission)}</td>
                </tr>
              ))}
              {(!data || data.data.length === 0) && (
                <tr><td colSpan="6" className="p-10 text-center text-sm text-gray-500">Belum ada transaksi dengan makelar di bulan {monthLabel(month)}</td></tr>
              )}
              {data && data.data.length > 0 && (
                <tr className="bg-gray-50 border-t-2 border-gray-900">
                  <td className="p-4 font-bold uppercase tracking-widest text-xs">TOTAL</td>
                  <td className="p-4 text-right font-mono-num font-bold">{total.sale}</td>
                  <td className="p-4 text-right font-mono-num font-bold">{total.purchase}</td>
                  <td className="p-4"></td>
                  <td className="p-4"></td>
                  <td className="p-4 text-right font-mono-num font-bold text-[#F97316]">{formatIDR(total.commission)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
